/** Debe coincidir con el backend (server.py) y nginx (client_max_body_size). */
export const IMAGE_UPLOAD_MAX_BYTES = 15 * 1024 * 1024;
export const IMAGE_UPLOAD_MAX_MB = 15;

export const IMAGE_UPLOAD_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp';

export const IMAGE_UPLOAD_FORMATS_LABEL = 'JPG, PNG, GIF o WEBP';

/** Evidencias de seguimiento dental: fotos + PDF. */
export const CLINICAL_EVIDENCE_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,application/pdf,.pdf';

export const CLINICAL_EVIDENCE_FORMATS_LABEL = 'JPG, PNG, GIF, WEBP o PDF';

function formatBytesMb(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const mb = bytes / (1024 * 1024);
  if (mb < 0.1) return `${Math.round(bytes / 1024)} KB`;
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
}

/**
 * Validación en el navegador antes de subir (respuesta inmediata).
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateImageFile(file, maxBytes = IMAGE_UPLOAD_MAX_BYTES) {
  if (!file) {
    return { ok: false, message: 'Selecciona un archivo de imagen.' };
  }
  const type = (file.type || '').toLowerCase();
  const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (type && !allowed.includes(type)) {
    return {
      ok: false,
      message: `Formato no permitido (${file.type || 'desconocido'}). Usa ${IMAGE_UPLOAD_FORMATS_LABEL}.`,
    };
  }
  if (file.size > maxBytes) {
    return {
      ok: false,
      message: `La imagen pesa ${formatBytesMb(file.size)}; el máximo permitido es ${IMAGE_UPLOAD_MAX_MB} MB.`,
    };
  }
  if (file.size === 0) {
    return { ok: false, message: 'El archivo está vacío.' };
  }
  return { ok: true };
}

/** Fotos o PDF (seguimiento dental). */
export function validateClinicalEvidenceFile(file, maxBytes = IMAGE_UPLOAD_MAX_BYTES) {
  if (!file) {
    return { ok: false, message: 'Selecciona una foto o un PDF.' };
  }
  const type = (file.type || '').toLowerCase();
  const name = String(file.name || '').toLowerCase();
  const isPdf = type === 'application/pdf' || name.endsWith('.pdf');
  const isImage = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(type);
  if (type && !isPdf && !isImage) {
    return {
      ok: false,
      message: `Formato no permitido. Usa ${CLINICAL_EVIDENCE_FORMATS_LABEL}.`,
    };
  }
  if (!type && !isPdf) {
    return {
      ok: false,
      message: `Formato no permitido. Usa ${CLINICAL_EVIDENCE_FORMATS_LABEL}.`,
    };
  }
  if (file.size > maxBytes) {
    return {
      ok: false,
      message: `El archivo pesa ${formatBytesMb(file.size)}; el máximo permitido es ${IMAGE_UPLOAD_MAX_MB} MB.`,
    };
  }
  if (file.size === 0) {
    return { ok: false, message: 'El archivo está vacío.' };
  }
  return { ok: true };
}

function detailFromResponseData(data) {
  if (data == null) return '';
  if (typeof data === 'string') return data.trim();
  const detail = data.detail;
  if (typeof detail === 'string') return detail.trim();
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item.msg === 'string') return item.msg;
        return '';
      })
      .filter(Boolean)
      .join(' ');
  }
  if (typeof data.message === 'string') return data.message.trim();
  return '';
}

/**
 * Mensaje legible a partir de un error de axios al subir imagen.
 */
export function formatImageUploadError(
  error,
  { maxMb = IMAGE_UPLOAD_MAX_MB } = {}
) {
  const status = error?.response?.status;
  const detail = detailFromResponseData(error?.response?.data);
  if (detail) return detail;

  if (status === 413) {
    return `El archivo supera el límite del servidor (máximo ${maxMb} MB). Comprime la imagen o elige otra más pequeña.`;
  }
  if (status === 401 || status === 403) {
    return 'Sesión expirada o sin permiso. Vuelve a iniciar sesión e inténtalo de nuevo.';
  }
  if (status === 404) {
    return 'Producto no encontrado. Recarga la página e inténtalo de nuevo.';
  }
  if (status === 400) {
    return `No se pudo subir la imagen. Revisa formato (${IMAGE_UPLOAD_FORMATS_LABEL}) y tamaño (máx. ${maxMb} MB).`;
  }
  if (status >= 500) {
    return 'Error del servidor al guardar la imagen. Inténtalo más tarde o contacta soporte.';
  }
  if (error?.message === 'Network Error') {
    return 'No hay conexión con el servidor. Revisa tu red e inténtalo de nuevo.';
  }
  return `No se pudo guardar la imagen. Formatos: ${IMAGE_UPLOAD_FORMATS_LABEL}. Tamaño máximo: ${maxMb} MB.`;
}
